import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';
import { ExtratoDesligamentoTokenResponse } from '../../../core/models/extrato-desligamento';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { Steps } from '../../../shared/components/steps/steps';
import { Router } from '@angular/router';

@Component({
  selector: 'app-extrato-desligamento',
  imports: [PdfDocument, Steps],
  styleUrl: './extrato-desligamento.scss',
  templateUrl: './extrato-desligamento.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ExtratoDesligamento implements OnInit {
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  readonly pdfSrc = signal<string | undefined>(undefined);
  readonly geradoEm = signal<string | undefined>(undefined);
  /** Texto ao lado do ícone de check no visualizador; vazio enquanto a API não informa a data. */
  readonly textoVisualizado = computed(() => {
    const geradoEm = this.geradoEm();
    return geradoEm ? `Visualizado em ${formatDate(geradoEm, "dd/MM/yyyy 'às' HH:mm", 'en-US')}` : '';
  });
  readonly stepAtual = signal(1);
  readonly totalSteps = signal(7);

  ngOnInit(): void {
    void this.getExtratoDesligamento();
  }

  onReturn() {
    this.router.navigate(['/instituto-online/home']);
  }

  onSubmit(){
    this.router.navigate(['/instituto-online/escolha-instituto']);
  }

  async download(): Promise<void> {
    const endereco = this.pdfSrc();

    if (!endereco) {
      this.toastService.show('Não há extrato disponível para download.', 'error');
      return;
    }

    try {
      await this.baixarArquivo(endereco);
    } catch {
      this.toastService.show('Não foi possível baixar o extrato.', 'error');
    }
  }

  private async getExtratoDesligamento(): Promise<void> {
    this.loaderService.showLoader('Gerando extrato...');

    try {
      const { body }: HttpResponse<ExtratoDesligamentoTokenResponse> =
        await this.httpClientService.gerarExtratoDesligamentoPorToken();
      this.pdfSrc.set(body?.urlExtrato);
      this.geradoEm.set(body?.solicitacaoIniciadaEm);
    } catch (erro) {
      const semTokenValido = erro instanceof HttpErrorResponse && (erro.status === 401 || erro.status === 403);
      this.toastService.show(
        semTokenValido
          ? 'Não foi possível identificar os dados do token.'
          : 'Não foi possível gerar o extrato.',
        'error',
      );
    } finally {
      this.loaderService.hideLoader();
    }
  }

  private async baixarArquivo(endereco: string): Promise<void> {
    const resposta = await fetch(endereco);
    const blob = await resposta.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'extrato-desligamento.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(objectUrl);
  }
}
